import React from 'react';
import { useCopperOxideStore } from '../store/useCopperOxideStore';

export function CopperOxideTable() {
  const results = useCopperOxideStore(s => s.results);
  const sorted = [...results].sort((a, b) => a.trialId - b.trialId);

  const avgRatio = sorted.length > 0
    ? sorted.reduce((acc, r) => acc + r.ratioCuToO, 0) / sorted.length
    : 0;

  return (
    <div className="flex flex-col h-full">
      <h2 className="text-lg font-bold mb-3">Trial Results</h2>
      {sorted.length === 0 && (
        <p className="text-sm text-muted-foreground">
          Complete a trial and press <em>Record &amp; Calculate</em> to populate this table.
        </p>
      )}
      {sorted.length > 0 && (
        <div className="overflow-x-auto rounded-lg border border-border">
          <table className="w-full text-xs">
            <thead className="bg-muted/40 text-[11px]">
              <tr>
                <th className="p-2 text-left font-semibold">Trial</th>
                <th className="p-2 text-left font-semibold">Empty (g)</th>
                <th className="p-2 text-left font-semibold">Tube+CuO (g)</th>
                <th className="p-2 text-left font-semibold">Tube+Cu (g)</th>
                <th className="p-2 text-left font-semibold">CuO (g)</th>
                <th className="p-2 text-left font-semibold">Cu (g)</th>
                <th className="p-2 text-left font-semibold">O lost (g)</th>
                <th className="p-2 text-left font-semibold">n(Cu)</th>
                <th className="p-2 text-left font-semibold">n(O)</th>
                <th className="p-2 text-left font-semibold">Cu : O</th>
                <th className="p-2 text-left font-semibold">Formula</th>
              </tr>
            </thead>
            <tbody>
              {sorted.map(r => (
                <tr key={r.trialId} className="border-t border-border">
                  <td className="p-2 font-mono">{r.trialId}</td>
                  <td className="p-2 font-mono">{r.emptyTubeMassG.toFixed(2)}</td>
                  <td className="p-2 font-mono">{r.tubeWithCuOMassG.toFixed(2)}</td>
                  <td className="p-2 font-mono">{r.tubeWithCuMassG.toFixed(2)}</td>
                  <td className="p-2 font-mono">{r.cuOMassG.toFixed(2)}</td>
                  <td className="p-2 font-mono text-emerald-600 dark:text-emerald-400">{r.cuMassG.toFixed(3)}</td>
                  <td className="p-2 font-mono text-amber-600 dark:text-amber-400">{r.oxygenLostG.toFixed(3)}</td>
                  <td className="p-2 font-mono">{r.molesCu.toFixed(4)}</td>
                  <td className="p-2 font-mono">{r.molesO.toFixed(4)}</td>
                  <td className="p-2 font-mono font-bold">1 : {r.ratioCuToO.toFixed(2)}</td>
                  <td className="p-2 font-mono font-bold">{r.empiricalFormula}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {sorted.length > 0 && (
        <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/30">
            <p className="text-[10px] uppercase tracking-wider text-amber-600 dark:text-amber-400 font-bold mb-1">
              Average Mole Ratio
            </p>
            <p className="font-mono text-lg font-bold">1 : {avgRatio.toFixed(2)}</p>
          </div>
          <div className="p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/30">
            <p className="text-[10px] uppercase tracking-wider text-emerald-600 dark:text-emerald-400 font-bold mb-1">
              Empirical Formula
            </p>
            <p className="font-mono text-lg font-bold">
              {avgRatio >= 0.97 && avgRatio <= 1.03 ? 'CuO' : `CuO${avgRatio.toFixed(2)}`}
            </p>
          </div>
        </div>
      )}

      <div className="mt-4 p-3 rounded-lg bg-card border border-border text-xs text-muted-foreground space-y-1">
        <p><span className="font-bold">Mr(Cu)</span> = 64 g mol⁻¹</p>
        <p><span className="font-bold">Mr(O)</span> = 16 g mol⁻¹</p>
        <p><span className="font-bold">Mr(CuO)</span> = 80 g mol⁻¹</p>
        <p>The ratio of moles Cu : O determines the empirical formula. A clean experiment gives 1 : 1, i.e. CuO.</p>
      </div>
    </div>
  );
}

export default CopperOxideTable;
